import { useMemo } from 'react';
import { useLocation } from 'react-router';
import { useUser } from '@/hooks/user/useUser';
import { useStatesAndTerritories } from '@/hooks/statesAndTerritories/useStatesAndTerritories';
import { isNonProdHost } from '@/lib/nonProdHost';
import { useHydrated } from '@/ssr/ssrHydration';
import type { ViewerContext } from '@/lib/visibility/evaluateVisibility';
import {
    applyVisibilityOverrides,
    resolveAgeBucket,
    resolveIdentity,
    resolvePageName,
} from '@/lib/visibility/visibilityContext';

/**
 * The query string as a rule should see it, with the `vis-` debug overrides removed.
 *
 * Without this, simulating a viewer changes the address the rules match on: a rule like
 * `^/library$` would stop matching as soon as any override was set, which looks like a
 * broken rule rather than a broken test harness.
 */
function visibleSearch(search: string): string {
    if (!search) return '';
    const params = new URLSearchParams(search);
    for (const key of [...params.keys()]) {
        if (key.startsWith('vis-')) params.delete(key);
    }
    const rest = params.toString();
    return rest ? `?${rest}` : '';
}

export interface VisibilityContextResult {
    context: ViewerContext;
    /**
     * False while the viewer is still unknown. Consumers must render nothing until this is
     * true: showing an item and then hiding it is the hydration flicker ASA-1570 forbids.
     */
    ready: boolean;
}

/**
 * Viewer attributes for CMS visibility rules.
 *
 * Signed-out viewers resolve immediately as `visitors` (useUser skips its query with no
 * session cookie). Signed-in viewers also wait on the states list, because the profile
 * stores `currentUsStateOrTerritoryId` and rules target two letter codes.
 *
 * SSR. A server render has no session and must not acquire one: its HTML is
 * cacheable and shared, so anything viewer-specific in it would be served to the wrong person.
 * It therefore always resolves the anonymous-visitor context, and so does the first client
 * render of a server-rendered document — otherwise the markup React hydrates onto would not
 * match what it draws. A signed-in viewer on such a page consequently sees the visitor view
 * for one render and then their own, which is the deliberate, scoped exception to the
 * no-flicker rule (visibility never shows an item and then hides it). Crawlers, which never
 * sign in, see no change at all.
 */
export const useVisibilityContext = (): VisibilityContextResult => {
    const { user, isLoading, isUninitialized } = useUser();
    const { states, isLoading: statesLoading, isError: statesError } = useStatesAndTerritories();
    const location = useLocation();
    const hydrated = useHydrated();

    const stateId = user?.userProfile?.currentUsStateOrTerritoryId;

    const stateCode = useMemo(() => {
        if (!stateId) return null;
        return states.find(state => state.id === stateId)?.abbreviation ?? null;
    }, [states, stateId]);

    // A signed-out viewer never resolves a user, so waiting on the user query would stall
    // forever; isUninitialized means the query was skipped (no session), which is an answer.
    const userReady = isUninitialized || !isLoading;
    // Only a signed-in viewer with a state id needs the lookup table. If that request fails
    // we proceed with an unknown state rather than blocking the page forever: state-targeted
    // rules then simply do not match, which is the same as a viewer with no state set.
    const stateReady = !stateId || statesError || !statesLoading;
    const ready = userReady && stateReady;

    const context = useMemo<ViewerContext>(() => {
        const anonymous = !hydrated;
        const base: ViewerContext = {
            page: resolvePageName(location.pathname),
            url: `${location.pathname}${visibleSearch(location.search)}`,
            identity: anonymous ? 'visitors' : resolveIdentity(user),
            ageBucket: anonymous ? null : resolveAgeBucket(user),
            stateCode: anonymous ? null : stateCode,
        };
        if (anonymous || !isNonProdHost()) {
            return base;
        }
        return applyVisibilityOverrides(base, location.search);
    }, [location.pathname, location.search, user, stateCode, hydrated]);

    // Before hydration the answer is already final — the viewer is anonymous by construction —
    // so consumers render content rather than the skeleton, which is the whole point: a
    // skeleton is what a crawler would otherwise index.
    return { context, ready: hydrated ? ready : true };
};
